import { useMemo, useState } from "react";
import { ArrowUturnLeftIcon, CheckIcon, DocumentDuplicateIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { toast } from "../../utils/toast";
import { Button, EmptyState, Input, RowAction, Select, Switch, Textarea } from "../ui";
import { useDraft } from "../../hooks/useDraft";
import { errorMessageOf, problemOf, problemMessage, scholarshipsApi } from "../../api/scholarships";
import { kindOf, useScholarshipChange } from "./model";
import ProgrammePicker, { useOfferedProgramme } from "./ProgrammePicker";
import { canEditSetUp } from "./access";
import ReasonField, { reasonErrorOf, withReason } from "./ReasonField";

const NO_CHANGES = {};
const MAX_DOCUMENTS = 30;
export const MB = 1024 * 1024;
// A document's size limit unless the HEC Admin picks another, as the API's default
const DEFAULT_MAX_BYTES = 10 * MB;

export const FILE_TYPES = [
  { value: "application/pdf", label: "PDF" },
  { value: "image/jpeg", label: "JPEG" },
  { value: "image/png", label: "PNG" },
];
export const SIZE_OPTIONS = [1, 2, 5, 10].map((mb) => ({ value: String(mb * MB), label: `${mb} MB` }));

// Documents most scholarships ask for, one click to add
export const COMMON = ["Passport", "National ID", "Academic transcript", "Degree certificate", "Admission letter", "CV", "Motivation letter", "Recommendation letter", "Research proposal"];

// The API's stable key for a document, e.g. "Recommendation letter" → RECOMMENDATION_LETTER
export const codeOf = (label) =>
  label
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 32);

export const newDocument = (label = "") => ({ code: "", label, description: "", mandatory: true, allowedTypes: ["application/pdf"], maxBytes: DEFAULT_MAX_BYTES });

// The API's requirement as the form edits it; `code` stays empty until saved, so it follows the label
export const requirementFormOf = (r) => ({
  code: r.code,
  label: r.label ?? "",
  description: r.description ?? "",
  mandatory: Boolean(r.mandatory),
  allowedTypes: r.allowedTypes ?? [],
  maxBytes: r.maxBytes ?? DEFAULT_MAX_BYTES,
});
export const requirementSpecOf = (d) => ({
  code: d.code || codeOf(d.label),
  label: d.label.trim(),
  ...(d.description.trim() && { description: d.description.trim() }),
  mandatory: d.mandatory,
  allowedTypes: d.allowedTypes,
  maxBytes: Number(d.maxBytes),
});
const sameList = (a, b) => JSON.stringify(a.map(requirementSpecOf)) === JSON.stringify(b.map(requirementSpecOf));

export const validateRequirements = (documents) => {
  const errors = {};
  const seen = new Map();
  documents.forEach((d, i) => {
    const code = d.code || codeOf(d.label);
    if (!d.label.trim()) errors[`${i}.label`] = "Document name is required";
    else if (!code) errors[`${i}.label`] = "Use at least one letter or number";
    else if (seen.has(code)) errors[`${i}.label`] = `Same as document ${seen.get(code) + 1}`;
    else seen.set(code, i);
    if (!d.allowedTypes.length) errors[`${i}.allowedTypes`] = "Allow at least one file type";
  });
  return errors;
};

/**
 * Journey step 2: the documents applicants upload. Each offered programme has its own list
 * (`PUT …/programmes/{programmeId}/requirements`); unsaved edits stay a draft per programme.
 */
export default function DocumentsTab({ scholarship }) {
  const { one } = kindOf(scholarship.type);
  const change = useScholarshipChange(scholarship.id);
  const { programmes, programme, choose } = useOfferedProgramme(scholarship);

  const [draft, setDraft, clearDraft] = useDraft(`scholarship-documents:${scholarship.id}:${programme?.id}`, NO_CHANGES);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState(null);
  const [saving, setSaving] = useState(false);

  const editable = canEditSetUp(scholarship);
  const saved = useMemo(() => (programme?.requirements ?? []).map(requirementFormOf), [programme]);
  const documents = draft.documents ?? saved;
  const reason = draft.reason ?? "";
  const changed = !sameList(documents, saved);

  const setDocuments = (next) => {
    setServerError(null);
    setDraft((prev) => (sameList(next, saved) ? (prev.reason ? { reason: prev.reason } : NO_CHANGES) : { ...prev, documents: next }));
  };
  const updateDocument = (index, field, value) => {
    setDocuments(documents.map((d, i) => (i === index ? { ...d, [field]: value } : d)));
    if (errors[`${index}.${field}`]) setErrors((prev) => ({ ...prev, [`${index}.${field}`]: undefined }));
  };
  const toggleType = (index, type) => {
    const current = documents[index].allowedTypes;
    updateDocument(index, "allowedTypes", current.includes(type) ? current.filter((t) => t !== type) : [...current, type]);
  };
  const add = (label) => documents.length < MAX_DOCUMENTS && setDocuments([...documents, newDocument(label)]);
  const remove = (index) => {
    setErrors({});
    setDocuments(documents.filter((_, i) => i !== index));
  };
  // Copies keep the source's codes, so the same document has the same key in every programme
  const copyFrom = (source) => {
    const present = new Set(documents.map((d) => d.code || codeOf(d.label)));
    const copies = source.requirements.filter((r) => !present.has(r.code)).map(requirementFormOf);
    setDocuments([...documents, ...copies].slice(0, MAX_DOCUMENTS));
    toast.success(copies.length ? `Copied ${copies.length} document${copies.length === 1 ? "" : "s"} from ${source.name}` : "Those documents are already in the list");
  };

  const discard = () => {
    clearDraft();
    setErrors({});
    setServerError(null);
  };

  const save = async () => {
    const found = validateRequirements(documents);
    const reasonError = reasonErrorOf(scholarship, reason);
    if (reasonError) found.reason = reasonError;
    setErrors(found);
    if (Object.keys(found).length) return;

    const body = withReason(scholarship, { requirements: documents.map(requirementSpecOf) }, reason);
    setSaving(true);
    try {
      await change((etag) => scholarshipsApi.put(`admin/scholarships/${scholarship.id}/programmes/${programme.id}/requirements`, body, { ifMatch: etag }));
      toast.success(`Documents for ${programme.name} saved`);
      discard();
    } catch (error) {
      console.error(`Failed to save documents of programme ${programme.id} on scholarship ${scholarship.id}:`, error);
      const problem = problemOf(error);
      setServerError(problem?.errors?.length ? problem.errors.map((e) => problemMessage(e.code) ?? e.code).join(" ") : null);
      toast.error(errorMessageOf(error, "Failed to save the documents. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  if (!programmes.length) {
    return <EmptyState title="No programmes yet" description={`Add the programmes this ${one} offers first; each programme has its own required documents.`} />;
  }

  return (
    <div className="flex flex-col gap-6">

      <ProgrammePicker
        programmes={programmes}
        programme={programme}
        onChange={(id) => {
          choose(id);
          setErrors({});
          setServerError(null);
        }}
        countOf={(p) => (p.requirements ?? []).length}
        item="document"
        copyLabel="Copy documents from"
        onCopy={editable ? copyFrom : null}
        disabled={saving}
      />

      <section className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold text-slate-700">
          Required documents ({documents.length}){changed && <span className="ml-2 font-normal text-amber-700">Unsaved changes</span>}
        </h3>

        {documents.length === 0 && <p className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">No documents yet for this programme.</p>}

        {documents.map((d, i) => (
          <fieldset key={i} disabled={!editable || saving} className="grid grid-cols-1 gap-4 rounded-lg border border-slate-100 p-4 md:grid-cols-12">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-500 md:col-span-12">
              <span>Document {i + 1}</span>
              {editable && <RowAction Icon={TrashIcon} label={`Remove ${d.label || "this document"}`} onClick={() => remove(i)} className="ml-auto" />}
            </div>
            <Input label="Document name" required maxLength={200} value={d.label} onChange={(e) => updateDocument(i, "label", e.target.value)} error={errors[`${i}.label`]} className="md:col-span-5" />
            <Select
              label="Maximum size"
              value={String(d.maxBytes)}
              onChange={(e) => updateDocument(i, "maxBytes", Number(e.target.value))}
              options={SIZE_OPTIONS.some((o) => o.value === String(d.maxBytes)) ? SIZE_OPTIONS : [{ value: String(d.maxBytes), label: `${(d.maxBytes / MB).toFixed(1)} MB` }, ...SIZE_OPTIONS]}
              className="md:col-span-3"
            />
            <div className="flex flex-col gap-2 md:col-span-4">
              <span className="text-sm font-medium text-slate-700">Required to submit</span>
              <div className="flex h-[46px] items-center gap-3">
                <Switch checked={d.mandatory} onChange={(value) => updateDocument(i, "mandatory", value)} label="Required to submit" disabled={!editable || saving} />
                <span className="text-sm text-slate-600">{d.mandatory ? "Required" : "Optional"}</span>
              </div>
            </div>
            <div className="flex flex-col gap-2 md:col-span-12">
              <span className="text-sm font-medium text-slate-700">Accepted file types</span>
              <div className="flex flex-wrap gap-4">
                {FILE_TYPES.map((t) => (
                  <label key={t.value} className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" className="h-4 w-4 accent-brand" checked={d.allowedTypes.includes(t.value)} onChange={() => toggleType(i, t.value)} />
                    {t.label}
                  </label>
                ))}
              </div>
              {errors[`${i}.allowedTypes`] && <p className="text-sm text-red-600">{errors[`${i}.allowedTypes`]}</p>}
            </div>
            <Textarea
              label="Notes for applicants"
              rows={2}
              maxLength={1000}
              placeholder="e.g. Certified copy, translated into English"
              value={d.description}
              onChange={(e) => updateDocument(i, "description", e.target.value)}
              className="md:col-span-12"
            />
          </fieldset>
        ))}

        {editable && documents.length < MAX_DOCUMENTS && (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" icon={<PlusIcon className="h-4 w-4" />} onClick={() => add("")} disabled={saving}>
              Add document
            </Button>
            {COMMON.filter((label) => !documents.some((d) => (d.code || codeOf(d.label)) === codeOf(label))).map((label) => (
              <Button key={label} variant="ghost" size="sm" icon={<DocumentDuplicateIcon className="h-4 w-4" />} onClick={() => add(label)} disabled={saving}>
                {label}
              </Button>
            ))}
          </div>
        )}
      </section>

      {editable && (
        <div className="flex flex-col gap-4 border-t border-slate-100 pt-5">
          <ReasonField
            scholarship={scholarship}
            value={reason}
            onChange={(value) => {
              setServerError(null);
              setErrors((prev) => ({ ...prev, reason: undefined }));
              setDraft((prev) => ({ ...prev, reason: value }));
            }}
            error={errors.reason}
            disabled={saving}
          />
          {serverError && <p className="text-sm text-red-600">{serverError}</p>}
          <div className="flex flex-col-reverse justify-end gap-3 sm:flex-row">
            <Button variant="ghost" onClick={discard} disabled={saving || (!changed && !reason)} icon={<ArrowUturnLeftIcon className="h-4 w-4" />}>
              Discard changes
            </Button>
            <Button onClick={save} loading={saving} disabled={!changed} icon={<CheckIcon className="h-4 w-4" />}>
              Save documents
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
